import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { SsrlocalstoreService } from '../_services/ssr-localstore.service';
import { v4 } from 'uuid';
import { environment } from 'src/environments/environment';

export interface HeaderOptions {
    type: 'auth' | 'basic' | 'nonAuthChat';
    fingerprint?: string;
    identifier?: string;
    responseTye?: string;
}

@Injectable({
    providedIn: 'root'
})
export class bleachrHTTP {
    private defaultHeaders: HeaderOptions = {
        type: 'basic',
        identifier: 'golf-one'
    };

    private fingerprint: string = '';

    constructor(
        private http: HttpClient,
        private ssr: SsrlocalstoreService
    ) {
        const stored = this.ssr.getItem('golfONE-fingerprint');
        this.fingerprint = stored ? stored : `web-${v4()}`;

        if (this.ssr.isClientSide && !stored) {
            this.ssr.setItem('golfONE-fingerprint', this.fingerprint);
        }
    }

    private basicHeaders(identifier?: string): { headers: HttpHeaders } {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Bleachr': identifier || environment.identifier,
                fingerprint: this.fingerprint
            })
        };
        return httpOptions;
    }

    public authHeaders(identifier?: string, responseType?: string): { headers: HttpHeaders; responseType?: string } {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Bleachr': identifier || environment.identifier,
                Authorization: `Token token=${this.ssr.getJWT()}`,
                fingerprint: this.fingerprint
            }),
            ...(responseType ? { responseType } : {})
        };
        return httpOptions;
    }

    private nonAuthChatHeaders(identifier?: string): { headers: HttpHeaders } {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'X-Bleachr': identifier || environment.identifier,
                fingerprint: this.fingerprint
            })
        };
        return httpOptions;
    }

    async get<request_type>(url: string, options: HeaderOptions = this.defaultHeaders): Promise<request_type> {
        const header: { headers: HttpHeaders } =
            options?.type === 'auth' ? this.authHeaders(options.identifier) : this.basicHeaders(options.identifier);

        const request = this.http.get<request_type>(url, header);
        return await lastValueFrom(request);
    }

    async post<request_type, payload_type>(
        url: string,
        payload: payload_type,
        options: HeaderOptions = this.defaultHeaders
    ): Promise<request_type> {
        const header =
            options?.type === 'auth'
                ? this.authHeaders(options.identifier, options?.responseTye)
                : options?.type === 'nonAuthChat'
                    ? this.nonAuthChatHeaders(options.identifier)
                    : this.basicHeaders(options.identifier);

        const request = this.http.post<request_type>(url, payload, header);
        return await lastValueFrom(request);
    }

    async put<request_type, payload_type>(
        url: string,
        payload: payload_type,
        options: HeaderOptions = this.defaultHeaders
    ): Promise<request_type> {
        const header: { headers: HttpHeaders } =
            options?.type === 'auth' ? this.authHeaders(options.identifier) : this.basicHeaders(options.identifier);

        const request = this.http.put<request_type>(url, payload, header);
        return await lastValueFrom(request);
    }

    async delete<request_type>(
        url: string,
        options: HeaderOptions = this.defaultHeaders,
        payload?: any
    ): Promise<request_type> {
        const header: { headers: HttpHeaders; body?: any } =
            options?.type === 'auth' ? this.authHeaders(options.identifier) : this.basicHeaders(options.identifier);

        if (payload) header.body = payload;

        const request = this.http.delete<request_type>(url, header);
        return await lastValueFrom(request);
    }
}
